import { Component, useEffect, type ReactNode } from 'react';
import { player } from '../../player/engine';
import { ErrorView, IconButton } from '../primitives';
import { resetFailedScreens } from './lazyScreen';
import styles from './LayerErrorBoundary.module.css';

/**
 * The way out of a failed layer: Back for a pushed page or an overlay, Close
 * for a modal layer (which Escape also closes, as the screen it replaces
 * would). Without it a crashed page left only Retry on screen, and nothing to
 * leave by in an installed iOS app, which has no browser Back.
 */
export interface LayerExit {
  kind: 'back' | 'close';
  onLeave: () => void;
}

interface Props {
  children: ReactNode;
  exit?: LayerExit;
}

interface State {
  error: unknown;
}

/** A lazily loaded chunk failed to load (offline, flaky network, or gone after a new deployment). */
function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|Importing a module script failed|Loading chunk|error loading dynamically|Unable to preload CSS/i.test(message);
}

/** Escape closes a failed modal layer, as it closes the screen it stands in for. */
function CloseOnEscape({ onLeave }: { onLeave: () => void }): null {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      event.preventDefault();
      onLeave();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onLeave]);
  return null;
}

/**
 * Keeps one crashing screen from taking down the shell; Retry remounts it.
 * React logs the error through the root's `onCaughtError` (errorReporter.ts).
 *
 * A screen whose chunk failed retries the download in place, so playback and
 * the rest of the app keep going. Only when the chunk keeps failing while
 * online (it is gone after a deployment) does Retry reload the page, and
 * never while audio is playing: then it keeps retrying in place.
 *
 * Pushed pages, overlays and modal layers pass an `exit`: the error view then
 * has a Back (or Close) button in the top bar's leading slot.
 */
export class LayerErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  /** Chunk-load failures since this boundary mounted. */
  private chunkFailures = 0;

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  componentDidCatch(error: unknown): void {
    if (isChunkLoadError(error)) this.chunkFailures += 1;
  }

  private retry = () => {
    if (!isChunkLoadError(this.state.error)) {
      this.setState({ error: null });
      return;
    }
    const online = typeof navigator === 'undefined' || navigator.onLine !== false;
    if (this.chunkFailures > 1 && online && !player.store.get().isPlaying) {
      window.location.reload();
      return;
    }
    resetFailedScreens();
    this.setState({ error: null });
  };

  render() {
    if (this.state.error == null) return this.props.children;
    const chunk = isChunkLoadError(this.state.error);
    const { exit } = this.props;
    return (
      <div className={styles.fallback}>
        {exit && (
          <div className={[styles.bar, exit.kind === 'close' ? styles.barModal : ''].filter(Boolean).join(' ')}>
            <IconButton
              icon={exit.kind === 'close' ? 'xmark' : 'back'}
              label={exit.kind === 'close' ? 'Close' : 'Back'}
              onClick={exit.onLeave}
            />
            {exit.kind === 'close' && <CloseOnEscape onLeave={exit.onLeave} />}
          </div>
        )}
        <ErrorView
          title={chunk ? 'Unable to load this screen' : undefined}
          message={chunk ? 'Check your connection and try again.' : undefined}
          error={chunk ? undefined : this.state.error}
          onRetry={this.retry}
        />
      </div>
    );
  }
}
